import { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react'
import { AlertTriangle, Info, AlertCircle, HelpCircle, X } from 'lucide-react'

// Confirm modal types
export type ConfirmType = 'danger' | 'warning' | 'info' | 'question'

export interface ConfirmOptions {
  title?: string
  message: string
  type?: ConfirmType
  confirmText?: string
  cancelText?: string
  confirmButtonClassName?: string
}

interface ConfirmContextValue {
  confirm: (options: ConfirmOptions | string) => Promise<boolean>
}

const ConfirmContext = createContext<ConfirmContextValue | undefined>(undefined)

// Config for different confirm types
const confirmConfig: Record<ConfirmType, { icon: typeof AlertTriangle; iconBgColor: string; iconColor: string; defaultConfirmClass: string }> = {
  danger: {
    icon: AlertTriangle,
    iconBgColor: 'bg-red-100',
    iconColor: 'text-red-600',
    defaultConfirmClass: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500',
  },
  warning: {
    icon: AlertCircle,
    iconBgColor: 'bg-yellow-100',
    iconColor: 'text-yellow-600',
    defaultConfirmClass: 'bg-yellow-600 text-white hover:bg-yellow-700 focus:ring-yellow-500',
  },
  info: {
    icon: Info,
    iconBgColor: 'bg-blue-100',
    iconColor: 'text-blue-600',
    defaultConfirmClass: 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500',
  },
  question: {
    icon: HelpCircle,
    iconBgColor: 'bg-primary-100',
    iconColor: 'text-primary-600',
    defaultConfirmClass: 'bg-primary-600 text-white hover:bg-primary-700 focus:ring-primary-500',
  },
}

interface ConfirmState {
  isOpen: boolean
  options: ConfirmOptions
  resolve: ((value: boolean) => void) | null
}

// Confirm modal component
function ConfirmModalComponent({
  isOpen,
  options,
  onConfirm,
  onCancel,
}: {
  isOpen: boolean
  options: ConfirmOptions
  onConfirm: () => void
  onCancel: () => void
}) {
  const confirmButtonRef = useRef<HTMLButtonElement>(null)
  const type = options.type ?? 'question'
  const config = confirmConfig[type]
  const Icon = config.icon

  // Focus the confirm button when modal opens
  useEffect(() => {
    if (isOpen && confirmButtonRef.current) {
      confirmButtonRef.current.focus()
    }
  }, [isOpen])

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onCancel()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = ''
    }
  }, [isOpen, onCancel])

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/50"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onCancel()
        }
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-title"
    >
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className={`flex-shrink-0 flex items-center justify-center w-12 h-12 rounded-full ${config.iconBgColor}`}>
              <Icon className={`h-6 w-6 ${config.iconColor}`} />
            </div>
            <div className="flex-1 min-w-0">
              {options.title && (
                <h3 id="confirm-title" className="text-lg font-semibold text-gray-900 mb-1">
                  {options.title}
                </h3>
              )}
              <p className="text-sm text-gray-600">{options.message}</p>
            </div>
            <button
              onClick={onCancel}
              className="flex-shrink-0 text-gray-400 hover:text-gray-500 transition-colors"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
        <div className="flex gap-3 px-6 pb-6 pt-2">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
          >
            {options.cancelText ?? 'Cancel'}
          </button>
          <button
            ref={confirmButtonRef}
            onClick={onConfirm}
            className={`flex-1 px-4 py-2 text-sm font-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors ${
              options.confirmButtonClassName ?? config.defaultConfirmClass
            }`}
          >
            {options.confirmText ?? 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  )
}

// Confirm provider component
export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<ConfirmState>({
    isOpen: false,
    options: { message: '' },
    resolve: null,
  })

  const confirm = useCallback((optionsOrMessage: ConfirmOptions | string): Promise<boolean> => {
    const options: ConfirmOptions =
      typeof optionsOrMessage === 'string' ? { message: optionsOrMessage } : optionsOrMessage

    return new Promise((resolve) => {
      setState({
        isOpen: true,
        options,
        resolve,
      })
    })
  }, [])

  const handleConfirm = useCallback(() => {
    state.resolve?.(true)
    setState((prev) => ({ ...prev, isOpen: false, resolve: null }))
  }, [state.resolve])

  const handleCancel = useCallback(() => {
    state.resolve?.(false)
    setState((prev) => ({ ...prev, isOpen: false, resolve: null }))
  }, [state.resolve])

  const value: ConfirmContextValue = { confirm }

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      <ConfirmModalComponent
        isOpen={state.isOpen}
        options={state.options}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </ConfirmContext.Provider>
  )
}

// Hook to use confirm modal
export function useConfirm(): (options: ConfirmOptions | string) => Promise<boolean> {
  const context = useContext(ConfirmContext)
  if (context === undefined) {
    throw new Error('useConfirm must be used within a ConfirmProvider')
  }
  return context.confirm
}

export default ConfirmProvider
